import React from "react";
import { Link } from "react-router-dom";
import { BookOpen, Moon, Puzzle, Scissors, Shapes, Sparkle, Star } from "lucide-react";
import { cn } from "../../lib/utils";

/* ============================================================
   SewingShell — the shared frame for the sewing sub-site.

   The sub-site is a hub (/sewing-primitives) plus three rooms
   (lab / learn / make), same shape as the synth lab. The shell
   carries the dreamy background, the drifting doodles, and the
   room switcher, so each page is just its own content.
   ============================================================ */

const NAV = [
  { id: "overview", label: "overview", to: "/sewing-primitives", Icon: Scissors },
  { id: "lab", label: "the lab", to: "/sewing-primitives/lab", Icon: Shapes },
  { id: "learn", label: "learn", to: "/sewing-primitives/learn", Icon: BookOpen },
  { id: "make", label: "make", to: "/sewing-primitives/make", Icon: Puzzle },
];

const DOODLES = [
  { Icon: Scissors, top: "12%", left: "8%", size: 64, opacity: 0.14, delay: "0s" },
  { Icon: Star, top: "20%", left: "86%", size: 44, opacity: 0.16, delay: "1.2s" },
  { Icon: Moon, top: "74%", left: "88%", size: 64, opacity: 0.12, delay: "0.6s" },
  { Icon: Sparkle, top: "82%", left: "7%", size: 40, opacity: 0.15, delay: "2s" },
];

const SewingShell = ({ active, title, intro, children }) => (
  <div className="relative min-h-[calc(100vh-4rem)] overflow-hidden bg-dreamy">
    <div
      aria-hidden
      className="pointer-events-none absolute -left-24 -top-24 h-96 w-96 rounded-full bg-primary/15 blur-3xl animate-aurora"
    />
    <div
      aria-hidden
      className="pointer-events-none absolute -bottom-40 -right-24 h-[28rem] w-[28rem] rounded-full bg-[hsl(var(--blue)/0.22)] blur-3xl animate-aurora"
      style={{ animationDelay: "-7s" }}
    />
    {DOODLES.map(({ Icon, top, left, size, opacity, delay }, i) => (
      <Icon
        key={i}
        aria-hidden
        className="pointer-events-none absolute text-primary animate-drift"
        style={{ top, left, width: size, height: size, opacity, animationDelay: delay }}
      />
    ))}

    <div className="container relative mx-auto max-w-5xl px-4 py-14">
      <p className="inline-flex items-center gap-1.5 text-sm font-medium text-primary">
        <Scissors className="h-4 w-4" />
        sewing primitives · soft geometry
      </p>

      <nav aria-label="Sewing primitives sections" className="mt-4 flex flex-wrap gap-2">
        {NAV.map(({ id, label, to, Icon }) => (
          <Link
            key={id}
            to={to}
            aria-current={id === active ? "page" : undefined}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-all",
              id === active
                ? "border-primary bg-primary text-primary-foreground shadow"
                : "border-border bg-card/60 text-muted-foreground hover:border-primary/50 hover:text-foreground"
            )}
          >
            <Icon className="h-4 w-4" />
            {label}
          </Link>
        ))}
      </nav>

      <h1 className="mt-6 text-3xl font-bold tracking-tight sm:text-5xl">
        <span className="text-gold-shimmer">{title}</span>
      </h1>
      {intro}

      {children}
    </div>
  </div>
);

export default SewingShell;
